import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from './Button'
import { Tooltip } from './Tooltip'
import './display-row.css'

/**
 * The band a reader's own options open onto, above the thing they change.
 *
 * A revealed ROW rather than a panel over it. These are settings somebody
 * changes while watching what they change, and a surface floating over the page
 * hides the very line being judged — the same reason the luach's toolbar reveals
 * its options in place instead of opening a popover.
 *
 * It is pinned, because a size or a key is reached for in the middle of a long
 * read and a row left at the top of the page is gone by then.
 *
 * **It holds no opinion about what is in it.** A book sets a face and a paper;
 * a song sets a key and a capo. What they share is the band, so that is what is
 * shared — a component carrying one screen's controls would be that screen's
 * component wearing a general name.
 *
 * Every control inside takes effect as it is pressed. There is nothing to
 * commit, because none of it is a draft: it is the state of the page you are
 * looking at, and the page is the confirmation.
 */
export function DisplayRow({
  open,
  onClose,
  children,
}: {
  open: boolean
  /**
   * A way out drawn IN the row, for a surface whose entry point cannot show
   * that it is open.
   *
   * Where the shell's header carries the action it draws the close itself, and
   * a second one here would be two ways out of one thing — the row keeps none.
   * A public reading copy has no header, only the strip, so it keeps this.
   */
  onClose?: () => void
  children: ReactNode
}) {
  return (
    /*
     * Always mounted, opened by an attribute: a row that is removed cannot
     * animate on its way out, so closing would be a jump and opening a slide.
     *
     * `inert` while shut, which is what keeps the controls out of the tab order
     * and away from a screen reader — height alone hides a thing from the eye
     * and from nobody else.
     */
    <div
      className="displayrow-slot"
      data-open={open ? '' : undefined}
      inert={!open}
    >
      <div className="displayrow">
        {children}
        {/* Drawn only where nothing else offers the way out — see `onClose`. */}
        {onClose && (
          <Tooltip label="Close display options">
            <Button
              icon
              variant="quiet"
              aria-label="Close display options"
              onClick={onClose}
              className="displayrow__close"
            >
              <X className="btn__icon" aria-hidden />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

/**
 * One named group of controls in the row.
 *
 * The caption names it on screen and the control's own `ariaLabel` names it to
 * a reader that never sees the caption; neither is redundant. A `<label>` would
 * be neither — it names an input, and a row of buttons is not one.
 */
export function DisplayField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="displayrow__field">
      <span className="displayrow__label">{label}</span>
      {children}
    </div>
  )
}
